import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';

interface TextInputProps {
    value: string | number;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    type?: 'text' | 'number' | 'email' | 'tel';
    label?: string;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    errorMessage?: string;
    numericOnly?: boolean;
    allowDecimal?: boolean; // 소수점 허용 옵션
    id?: string;
    /** 화면에 표시되는 label과 별개로, 필수값 안내 토스트 등 검증 메시지에 쓰일 이름.
     *  미지정 시 label을 대신 사용한다(외부에 별도 <label>을 두고 label prop은 안 쓰는 경우 대비). */
    validationLabel?: string;
}

const TextInput = ({
    value,
    onChange,
    placeholder = '',
    disabled = false,
    required = false,
    className = 'form-control',
    type = 'text',
    label,
    minLength,
    maxLength,
    pattern,
    errorMessage,
    numericOnly = false,
    allowDecimal = false,
    id,
    validationLabel,
}: TextInputProps) => {
    const [error, setError] = useState<string>('');
    const [touched, setTouched] = useState<boolean>(false);

    const validate = (val: string): string => {
        // required 체크
        if (required && !val.trim()) {
            return (
                errorMessage ||
                `${validationLabel || label || '이 항목'}을(를) 입력해주세요.`
            );
        }

        // numericOnly 체크
        if (numericOnly && val.length > 0) {
            const regex = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d+$/;
            if (!regex.test(val)) {
                return allowDecimal
                    ? '숫자와 소수점만 입력 가능합니다.'
                    : '숫자만 입력 가능합니다.';
            }
        }

        // minLength 체크
        if (minLength && val.length > 0 && val.length < minLength) {
            return `최소 ${minLength}자 이상 입력해주세요.`;
        }

        // maxLength 체크
        if (maxLength && val.length > maxLength) {
            return `최대 ${maxLength}자까지 입력 가능합니다.`;
        }

        // pattern 체크
        if (pattern && val.length > 0) {
            const regex = new RegExp(pattern);
            if (!regex.test(val)) {
                return errorMessage || '올바른 형식으로 입력해주세요.';
            }
        }

        return '';
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // numericOnly가 true일 때 숫자와 소수점만 허용
        if (numericOnly && newValue) {
            const regex = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d+$/;

            if (!regex.test(newValue)) {
                return;
            }

            // 소수점이 여러 개 입력되는 것 방지
            if (allowDecimal && (newValue.match(/\./g) || []).length > 1) {
                return;
            }
        }

        if (onChange) {
            onChange(newValue);
        }

        if (touched) {
            const validationError = validate(newValue);
            setError(validationError);
        }
    };

    // 키보드 입력 제어 (선택적)
    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (numericOnly) {
            const isNumber = /\d/.test(e.key);
            const isDecimal = allowDecimal && e.key === '.';
            const isMinus = e.key === '-';
            const isControlKey = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete',
            ].includes(e.key);

            if (!isNumber && !isDecimal && !isMinus && !isControlKey) {
                e.preventDefault();
            }

            // 소수점이 이미 있으면 추가 입력 방지
            if (isDecimal && String(value).includes('.')) {
                e.preventDefault();
            }

            // 마이너스는 맨 앞에만 입력 가능하도록
            if (
                isMinus &&
                (String(value).includes('-') ||
                    (e.currentTarget.selectionStart ?? 0) > 0)
            ) {
                e.preventDefault();
            }
        }
    };

    const handleBlur = () => {
        setTouched(true);
        const validationError = validate(String(value));
        setError(validationError);
    };

    useEffect(() => {
        if (touched) {
            const validationError = validate(String(value));
            setError(validationError);
        }
    }, [value, touched]);

    // 외부에서 유효성 검사 결과를 확인할 수 있도록 데이터 속성 추가
    const isValid = !validate(String(value));

    return (
        <div className="w-100">
            <input
                id={id}
                type={type}
                className={`${className} ${error ? 'is-invalid' : ''}`}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                onBlur={handleBlur}
                disabled={disabled}
                data-valid={isValid}
                data-required={required}
                data-label={validationLabel || label}
                inputMode={
                    numericOnly
                        ? allowDecimal
                            ? 'decimal'
                            : 'numeric'
                        : undefined
                }
            />
            {error && touched && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </div>
    );
};

export default TextInput;
